import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, MessageSquare, X } from 'lucide-react';
import {
  DEFAULT_VERSION_ID,
  resolveVersion,
  VERSIONS,
} from '@/versions/registry';
import { useComments } from './CommentContext';
import type { CommentResponse } from './types';

// Right-side panel listing every comment across every registered version,
// grouped by version then by top-level anchor inside each version. Independent
// of comment-mode — reviewers can browse here without entering review mode.
// Clicking an entry on a different version flips ?v=<id> first, then opens
// the thread once the target version has mounted.

const SIDEBAR_WIDTH = 320;

type ParentGroup = {
  parent: string;
  items: CommentResponse[];
};

type VersionGroup = {
  version: string;
  label: string;
  isCurrent: boolean;
  isLatest: boolean;
  openCount: number;
  total: number;
  parents: ParentGroup[];
};

// Group comments by version, then by top-level anchor inside each version.
// Version order matches the registry (newest-first); parents are alphabetical;
// inside a parent, open before resolved then newest first.
function groupByVersion(
  comments: CommentResponse[],
  currentVersion: string,
): VersionGroup[] {
  const buckets = new Map<string, CommentResponse[]>();
  for (const c of comments) {
    const arr = buckets.get(c.version) ?? [];
    arr.push(c);
    buckets.set(c.version, arr);
  }

  // Drive ordering off the registry so versions appear newest-first regardless
  // of API ordering. Any unknown version (shouldn't happen in normal use)
  // falls to the end alphabetically.
  const registryOrder = VERSIONS.map((v) => v.id);
  const orderedIds = [
    ...registryOrder.filter((id) => buckets.has(id)),
    ...Array.from(buckets.keys())
      .filter((id) => !registryOrder.includes(id))
      .sort(),
  ];

  return orderedIds.map((versionId) => {
    const items = buckets.get(versionId) ?? [];
    const parentMap = new Map<string, CommentResponse[]>();
    for (const c of items) {
      const parent = c.component.split('.')[0];
      const arr = parentMap.get(parent) ?? [];
      arr.push(c);
      parentMap.set(parent, arr);
    }
    const parents: ParentGroup[] = Array.from(parentMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([parent, items]) => ({
        parent,
        items: items.sort((a, b) => {
          if (a.state !== b.state) return a.state === 'open' ? -1 : 1;
          return a.createdAt < b.createdAt ? 1 : -1;
        }),
      }));

    const entry = resolveVersion(versionId);
    return {
      version: versionId,
      label: entry.label.split(' — ')[0],
      isCurrent: versionId === currentVersion,
      isLatest: versionId === DEFAULT_VERSION_ID,
      openCount: items.filter((c) => c.state === 'open').length,
      total: items.length,
      parents,
    };
  });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

export function CommentSidebar() {
  const {
    version: currentVersion,
    sidebarOpen,
    toggleSidebar,
    allComments,
    openThread,
    openThreadNumber,
    commentMode,
    toggleCommentMode,
  } = useComments();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const versionGroups = useMemo(
    () => groupByVersion(allComments, currentVersion),
    [allComments, currentVersion],
  );
  const openCount = allComments.filter((c) => c.state === 'open').length;
  const resolvedCount = allComments.length - openCount;

  if (!sidebarOpen) return null;

  function onItemClick(c: CommentResponse) {
    // If the comment is on a different version, flip the URL param first.
    // The version subtree re-mounts, so we defer the thread-open to the next
    // tick — pin layer / overlay are on the new tree by then. If we're
    // already on the right version, this is a no-op.
    if (c.version !== currentVersion) {
      const next = new URLSearchParams(params);
      next.set('v', c.version);
      setParams(next, { replace: false });
      // The new version's CommentProvider has its own state — we can't reach
      // into it from here. Reviewers landing on the other version will see
      // the pin; they can click it to open the thread.
      return;
    }
    if (c.route !== window.location.pathname) navigate(c.route);
    if (!commentMode) toggleCommentMode();
    openThread(c.number);
  }

  return (
    <aside
      data-comment-ui="true"
      className="glass-deep"
      style={{
        position: 'fixed',
        top: 48,
        right: 0,
        bottom: 0,
        width: SIDEBAR_WIDTH,
        zIndex: 9993,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
      }}
      aria-label="Comment list"
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: '1px solid var(--hairline)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MessageSquare size={14} style={{ color: 'var(--accent)' }} aria-hidden="true" />
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)' }}>
            Comments
          </span>
          <span
            style={{
              fontSize: 10.5,
              color: 'var(--ink-tertiary)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {openCount} open
            {resolvedCount > 0 ? ` · ${resolvedCount} resolved` : ''}
          </span>
        </div>
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label="Close comment sidebar"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            color: 'var(--ink-tertiary)',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <X size={14} aria-hidden="true" />
        </button>
      </header>

      <div
        className="scroll-soft"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px 8px 24px',
        }}
      >
        {versionGroups.length === 0 && (
          <div
            style={{
              padding: '24px 12px',
              textAlign: 'center',
              color: 'var(--ink-tertiary)',
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            No comments yet.
            <br />
            Toggle comment mode and click a marked surface to add one.
          </div>
        )}

        {versionGroups.map((vg) => (
          <section
            key={vg.version}
            style={{ marginBottom: 16, paddingBottom: 4 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px 6px',
                borderBottom: '1px solid var(--hairline)',
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--ink-primary)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {vg.label}
              </span>
              {vg.isLatest && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'var(--accent)',
                    border: '1px solid var(--accent)',
                    padding: '1px 5px',
                    borderRadius: 3,
                  }}
                >
                  Latest
                </span>
              )}
              {vg.isCurrent && !vg.isLatest && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'var(--ink-tertiary)',
                    border: '1px solid var(--hairline-strong)',
                    padding: '1px 5px',
                    borderRadius: 3,
                  }}
                >
                  Viewing
                </span>
              )}
              <span
                style={{
                  fontSize: 10,
                  color: 'var(--ink-tertiary)',
                  fontVariantNumeric: 'tabular-nums',
                  marginLeft: 'auto',
                }}
              >
                {vg.openCount} open
                {vg.total - vg.openCount > 0 ? ` · ${vg.total - vg.openCount} resolved` : ''}
              </span>
            </div>

            {vg.parents.map((group) => (
              <section key={`${vg.version}.${group.parent}`} style={{ marginBottom: 10 }}>
                <h3
                  style={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontWeight: 600,
                    color: 'var(--ink-tertiary)',
                    margin: '6px 10px 4px',
                  }}
                >
                  {group.parent} · {group.items.length}
                </h3>
                {group.items.map((c) => {
                  const isActive = openThreadNumber === c.number && vg.isCurrent;
                  const path = c.component.split('.').join(' › ');
                  const firstLine = c.body
                    .replace(/^\*\*From\*\*:[^\n]*\n\n?/, '')
                    .split('\n')[0]
                    .trim();
                  return (
                    <button
                      key={c.number}
                      type="button"
                      onClick={() => onItemClick(c)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        background: isActive ? 'var(--hairline)' : 'transparent',
                        border: '1px solid transparent',
                        borderRadius: 6,
                        padding: '8px 10px',
                        cursor: 'pointer',
                        marginBottom: 2,
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'var(--hairline)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 6,
                          marginBottom: 3,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 500,
                            color: 'var(--ink-secondary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {path}
                        </span>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 10,
                            color: 'var(--ink-tertiary)',
                          }}
                        >
                          {c.state === 'closed' && (
                            <Check size={11} aria-hidden="true" />
                          )}
                          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {timeAgo(c.createdAt)}
                          </span>
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: c.state === 'closed' ? 'var(--ink-tertiary)' : 'var(--ink-primary)',
                          textDecoration: c.state === 'closed' ? 'line-through' : 'none',
                          lineHeight: 1.4,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical' as const,
                          overflow: 'hidden',
                        }}
                      >
                        {firstLine}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: 'var(--ink-tertiary)',
                          marginTop: 3,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span>{c.author}</span>
                        {c.replyCount > 0 && (
                          <>
                            <span>·</span>
                            <span>↪ {c.replyCount}</span>
                          </>
                        )}
                      </div>
                    </button>
                  );
                })}
              </section>
            ))}
          </section>
        ))}
      </div>
    </aside>
  );
}
